import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import debounce from 'lodash/debounce';
import './ContrailVideo.css'; 

// Utility function for formatting dates in UTC
const formatDateUTC = (date) => {
    return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
};

const ContrailVideo = ({ currentDateTime, addCachedHour, cachedHours, isPlaying, visible }) => {
    const [videoUrls, setVideoUrls] = useState([]);
    const [loading, setLoading] = useState(false); // State for loading
    const dataCache = useRef({});
    const inProgressCache = useRef(new Set());

    const isDataEmpty = (data) => {
        if (Array.isArray(data)) {
            return data.length === 0;
        }
        if (typeof data === 'object' && data !== null) {
            return Object.keys(data).length === 0;
        }
        return !data; // This will catch null, undefined, '', 0, false
    };

    const fetchVideosForCurrentDate = useCallback(async (dateStr) => {
        // If data is already being fetched for this date, skip fetching
        if (inProgressCache.current.has(dateStr)) {
            return;
        }

        inProgressCache.current.add(dateStr);
        setLoading(true); // Start loading

        try {
            console.log("Fetching videos for:", dateStr);
            const response = await fetch(`http://localhost:8000/contrail_video?date_str=${dateStr}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();

            if (!isDataEmpty(data)) {
                dataCache.current[dateStr] = data;
                setVideoUrls(data.map((video) => video.url));
            } else {
                setVideoUrls([]);
            }
        } catch (error) {
            console.error(`Error fetching videos for ${dateStr}:`, error);
            setVideoUrls([]);
        } finally {
            setLoading(false); // Stop loading
            inProgressCache.current.delete(dateStr);
        }
    }, [addCachedHour]);

    const debouncedFetchVideosForCurrentDate = useMemo(
        () => debounce(fetchVideosForCurrentDate, 300),
        [fetchVideosForCurrentDate]
    );

    useEffect(() => {
        if (!visible) return;

        const currentDateStr = formatDateUTC(currentDateTime);

        // Fetch videos every time the currentDateTime changes
        debouncedFetchVideosForCurrentDate(currentDateStr);

        return () => {
            debouncedFetchVideosForCurrentDate.cancel();
        };
    }, [currentDateTime, visible, debouncedFetchVideosForCurrentDate]);

    if (!visible) {
        return null;
    }

    return (
        <div>
            {/* Loading Screen */}
            {loading && (
                <div className="loading-screen">
                    <div className="spinner"></div>
                </div>
            )}

            {/* Video Content */}
            {!loading && videoUrls.length > 0 ? (
                videoUrls.map((url, index) => (
                    <div key={index}>
                        <video 
                            key={url}
                            controls 
                            autoPlay={isPlaying && index === 0} 
                            style={{ width: '100%' }}
                        >
                            <source src={url} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                ))
            ) : (
                !loading && <p>No videos available for the selected date and time.</p>
            )}
        </div>
    );
};

export default ContrailVideo;
